import React, { useEffect, useState } from 'react'
import Chart from 'react-apexcharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The API labels a bucket two ways: `day` identifies it ("2026-08-14",
// "Week of 2026-08-10", "2026-08") and `weekday` is a human word for it
// ("Friday", "Week", "Aug 2026").
//
// The axis used to show `weekday`, which is fine for one week but unusable
// for a month: 31 points read "Saturday, Sunday, Monday..." four times over
// with no way to tell which Monday. Daily buckets now label by date.
const isDailyBucket = (day) => typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day);

const axisLabel = (item) => {
    if (!isDailyBucket(item.day)) return item.weekday;
    const [, month, date] = item.day.split('-');
    return `${Number(date)} ${MONTHS[Number(month) - 1]}`;
};

const tooltipLabel = (item) => {
    if (!isDailyBucket(item.day)) return item.weekday;
    return `${item.weekday}, ${axisLabel(item)}`;
};

const Engagement = ({ data, chartName }) => {
    const [trendData, setTrendData] = useState([]);

    useEffect(() => {
        if (data) {
            setTrendData(Array.isArray(data) ? data : [])
        }
    }, [data]);

    const categories = trendData.length ? trendData.map(axisLabel) : [];
    const seriesData = trendData.length ? trendData.map((item) => item.count) : [];
    const tooltipLabels = trendData.length ? trendData.map(tooltipLabel) : [];

    // A month of daily points cannot show 31 labels legibly, so thin them out
    // and let the tooltip carry the exact day.
    const tickAmount = categories.length > 14
        ? Math.min(10, Math.ceil(categories.length / 3))
        : undefined;

    const options = {
        chart: {
            type: 'area',
            zoom: { enabled: false },
            toolbar: { show: false },
        },
        xaxis: {
            categories: categories,
            tickAmount: tickAmount,
            tickPlacement: 'on',
            labels: {
                style: {
                    fontSize: '12px',
                    fontWeight: 400,
                    colors: '#667085',
                },
                offsetX: 0,
                offsetY: 5,
                rotate: -45,
                hideOverlappingLabels: true,
            },
        },
        tooltip: {
            x: {
                // Apex titles the tooltip with the (thinned) axis label, so the
                // exact day comes from the bucket itself.
                formatter: (_value, { dataPointIndex }) =>
                    tooltipLabels[dataPointIndex] ?? '',
            },
        },
        markers: {
            size: 0,
            hover: { size: 5 },
        },
        yaxis: {
            labels: {
                show: true,
                showDuplicates: false,
                align: 'right',
                minWidth: 0,
                maxWidth: 160,
                style: {
                    colors: ['#667085'],
                    fontSize: '15px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                },
                offsetX: -15,
                offsetY: 0,
                rotate: 0,
            },
        },
        zoom: {
            enabled: false,
            autoScaleYaxis: false,
        },
        colors: ['#c5003e'],
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'light',
                shadeIntensity: 1,
                gradientToColors: ['rgba(197, 0, 62, 0)'],
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 0,
                stops: [0, 95],
            },
        },
        stroke: {
            width: 2,
            curve: 'smooth',
        },
    };

    return (
        <div className='shadow-custom rounded-3xl p-5 w-full lg:w-1/3'>
            <p className='text-base font-semibold text-black mb-6'>{chartName}</p>
            <Chart
            className="mt-7"
                options={options}
                series={[{ name: "Trend", data: seriesData }]}
                type="area"
                width="100%"
                height="100%"
            />
        </div>
    )
}

export default Engagement